import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@mui/material";
import SignaturePad from "signature_pad";
import { useTranslation } from "react-i18next";
import {
  ModalContainer,
  ModalTitle,
  ModalSubtitle,
  StyledTabs,
  StyledTab,
  SignatureContainer,
  TypedSignatureContainer,
  SignatureInput,
  TypedSignaturePreview,
  SignatureText,
  CanvasContainer,
  SignatureCanvas,
  ClearButton,
  CanvasPlaceholder,
  ActionsContainer,
} from "./SignatureModal.styled";

type SignatureMethod = "click" | "draw";

interface SignatureModalContentProps {
  onSign: (signature: string, method: SignatureMethod) => void;
  onCancel: () => void;
  patientName?: string;
}

export const SignatureModalContent: React.FC<SignatureModalContentProps> = ({
  onSign,
  onCancel,
  patientName = "",
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<SignatureMethod>("click");
  const [typedSignature, setTypedSignature] = useState(patientName);
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);

  const initializeSignaturePad = useCallback(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const container = canvas.parentElement;

      if (container) {
        // Set canvas size to match container
        canvas.width = container.clientWidth;
        canvas.height = container.clientHeight;
      }

      // Clear existing SignaturePad if any
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
      }

      signaturePadRef.current = new SignaturePad(canvas, {
        backgroundColor: "rgb(255, 255, 255)",
        penColor: "rgb(27, 37, 47)",
      });

      signaturePadRef.current.addEventListener("endStroke", () => {
        setHasDrawnSignature(!signaturePadRef.current?.isEmpty());
      });
    }
  }, []);

  useEffect(() => {
    if (activeTab === "draw") {
      // Small delay to ensure DOM is ready
      const timeout = setTimeout(initializeSignaturePad, 100);
      return () => {
        clearTimeout(timeout);
        // Clean up SignaturePad when switching away
        if (signaturePadRef.current) {
          signaturePadRef.current.off();
          signaturePadRef.current = null;
        }
      };
    }
  }, [activeTab, initializeSignaturePad]);

  useEffect(() => {
    const handleResize = () => {
      if (
        canvasRef.current &&
        signaturePadRef.current &&
        activeTab === "draw"
      ) {
        const canvas = canvasRef.current;
        const container = canvas.parentElement;
        const data = signaturePadRef.current.toData();

        if (container) {
          canvas.width = container.clientWidth;
          canvas.height = container.clientHeight;
        }

        signaturePadRef.current.clear();
        if (data.length > 0) {
          signaturePadRef.current.fromData(data);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeTab]);

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setHasDrawnSignature(false);
    }
  };

  const handleSign = () => {
    if (activeTab === "click") {
      if (typedSignature.trim()) {
        onSign(typedSignature.trim(), "click");
      }
    } else {
      if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
        const signatureData = signaturePadRef.current.toDataURL("image/png");
        onSign(signatureData, "draw");
      }
    }
  };

  const isSignButtonDisabled =
    activeTab === "click" ? !typedSignature.trim() : !hasDrawnSignature;

  return (
    <ModalContainer>
      <ModalTitle>{t("signatureModal.title")}</ModalTitle>
      <ModalSubtitle>{t("signatureModal.subtitle")}</ModalSubtitle>

      <StyledTabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue as SignatureMethod)}
        variant="fullWidth"
      >
        <StyledTab value="click" label={t("signatureModal.clickToSign")} />
        <StyledTab value="draw" label={t("signatureModal.drawnSignature")} />
      </StyledTabs>

      <SignatureContainer>
        {activeTab === "click" ? (
          <TypedSignatureContainer>
            <SignatureInput
              type="text"
              placeholder={t(
                "signatureModal.typePlaceholder",
                "Type your name",
              )}
              value={typedSignature}
              onChange={(e) => setTypedSignature(e.target.value)}
              autoFocus
            />
            <TypedSignaturePreview>
              {typedSignature && (
                <SignatureText>{typedSignature}</SignatureText>
              )}
            </TypedSignaturePreview>
          </TypedSignatureContainer>
        ) : (
          <CanvasContainer>
            <SignatureCanvas ref={canvasRef} />
            {!hasDrawnSignature && (
              <CanvasPlaceholder>
                {t("signatureModal.drawPlaceholder")}
              </CanvasPlaceholder>
            )}
            {hasDrawnSignature && (
              <ClearButton onClick={handleClear}>
                {t("signatureModal.clear")}
              </ClearButton>
            )}
          </CanvasContainer>
        )}
      </SignatureContainer>

      <ActionsContainer>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            minWidth: { xs: "100%", sm: 150 },
          }}
        >
          {t("signatureModal.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSign}
          disabled={isSignButtonDisabled}
          sx={{
            minWidth: { xs: "100%", sm: 150 },
          }}
        >
          {t("signatureModal.sign")}
        </Button>
      </ActionsContainer>
    </ModalContainer>
  );
};

export default SignatureModalContent;
